import { useLayoutEffect, useRef, useState } from 'react';

type SectionDefinition = {
  id: string;
  label: string;
};

const EVENT_DETAIL_SECTIONS: SectionDefinition[] = [
  { id: 'event-overview-section', label: '概要' },
  { id: 'event-info-section', label: 'イベント情報' },
  { id: 'casting-section', label: '参加アーティスト' },
  { id: 'event-banner-section', label: 'イベントバナー' },
  { id: 'event-marketing-section', label: 'マーケティング' },
  { id: 'event-finance-section', label: 'イベント経理' },
  { id: 'event-group-section', label: 'グループイベント' },
  { id: 'event-task-section', label: 'イベントタスク' },
  { id: 'timetable-section', label: 'タイムテーブル' },
  { id: 'ticket-sales-section', label: '券売状況' },
  { id: 'stage-seating-section', label: 'ステージシーティング' },
  { id: 'event-materials-section', label: 'イベント資料' },
  { id: 'event-images-section', label: 'イベント画像' },
  { id: 'event-contracts-section', label: '契約状況' },
];

const sameSectionIds = (left: SectionDefinition[], right: SectionDefinition[]) =>
  left.length === right.length && left.every((section, index) => section.id === right[index]?.id);

export default function EventDetailSectionIndex() {
  const indexRef = useRef<HTMLElement | null>(null);
  const [visibleSections, setVisibleSections] = useState<SectionDefinition[]>([]);
  const [activeSectionId, setActiveSectionId] = useState('');

  useLayoutEffect(() => {
    const scrollRoot = indexRef.current?.closest<HTMLElement>('.event-detail-page');
    if (!scrollRoot) return undefined;

    const resolveVisibleSections = () => {
      const nextSections = EVENT_DETAIL_SECTIONS.filter(({ id }) =>
        Boolean(scrollRoot.querySelector(`#${id}`)),
      );
      setVisibleSections((current) => (sameSectionIds(current, nextSections) ? current : nextSections));
      return nextSections;
    };

    const updateActiveSection = () => {
      const sections = resolveVisibleSections();
      if (sections.length === 0) {
        setActiveSectionId('');
        return;
      }

      const rootRect = scrollRoot.getBoundingClientRect();
      const focusLine = rootRect.top + Math.min(128, Math.max(72, scrollRoot.clientHeight * 0.16));
      const isAtBottom =
        scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight <= 2;
      let nextActiveId = sections[0].id;

      for (const section of sections) {
        const element = scrollRoot.querySelector<HTMLElement>(`#${section.id}`);
        if (element && element.getBoundingClientRect().top <= focusLine) {
          nextActiveId = section.id;
        }
      }
      if (isAtBottom) nextActiveId = sections[sections.length - 1].id;
      setActiveSectionId((current) => (current === nextActiveId ? current : nextActiveId));
    };

    updateActiveSection();
    const sectionObserver = new MutationObserver(updateActiveSection);
    sectionObserver.observe(scrollRoot, { childList: true, subtree: true });
    scrollRoot.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);
    return () => {
      sectionObserver.disconnect();
      scrollRoot.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const scrollRoot = indexRef.current?.closest<HTMLElement>('.event-detail-page');
    const target = scrollRoot?.querySelector<HTMLElement>(`#${sectionId}`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (visibleSections.length === 0) {
    return <nav ref={indexRef} className="event-detail-section-index" aria-label="イベント詳細のセクション" />;
  }

  return (
    <nav ref={indexRef} className="event-detail-section-index" aria-label="イベント詳細のセクション">
      <ul className="event-detail-section-index__list">
        {visibleSections.map((section) => {
          const active = section.id === activeSectionId;
          return (
            <li key={section.id} className="event-detail-section-index__item">
              <button
                type="button"
                className={`event-detail-section-index__link${active ? ' is-active' : ''}`}
                aria-current={active ? 'location' : undefined}
                onClick={() => scrollToSection(section.id)}
              >
                {section.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export { EVENT_DETAIL_SECTIONS };
